import { HelpCircle } from 'lucide-react'
import React, { useState } from 'react'

import { Modal } from 'slab'

const reasonExplanations = {
  administrative_correction: {
    title: 'Administrative Correction',
    description: 'Use this when correcting errors in enrollment data, fixing system issues, or making adjustments due to administrative mistakes (on us or the carrier).',
    examples: ['Correcting incorrect monthly plan price', 'Fixing coverage dates', 'Resolving Thatch bugs']
  },
  qualifying_life_event: {
    title: 'Qualifying Life Event',
    description: 'Use this when a member experiences a qualifying life event (QLE) such as marriage, divorce, birth/adoption of a child, loss of other coverage, or change in employment status that allows them to make changes outside of open enrollment.',
    examples: ['Getting married or divorced', 'Having a baby or adopting a child', 'Losing job-based health coverage', 'Moving']
  },
  initial_ichra_invite: {
    title: 'Initial ICHRA Invite',
    description: 'Use this for new employees or members who are enrolling in ICHRA for the first time when they become eligible.',
    examples: ['New hire starting coverage', 'Existing employee becoming eligible']
  },
  open_enrollment: {
    title: 'Open Enrollment',
    description: 'Use this for changes made during the annual open enrollment period when members can freely make changes to their coverage.',
    examples: ['Annual plan changes', 'Adding or removing dependents during open enrollment', 'Adjusting prices during open enrollment']
  },
  termination: {
    title: 'Termination',
    description: 'Use this when ending or canceling coverage due to employment termination, voluntary cancellation, or other coverage-ending events.',
    examples: ['Employee leaving the company', 'End of ICHRA eligibility']
  },
  member_initiated_change: {
    title: 'Member Initiated Change',
    description: 'Use this for changes requested or made directly by the member outside of open enrollment, the ICHRA invite, or qualifying life event.',
    examples: ['Member canceled their plan', 'Member removed a dependent from their plan']
  },
  unknown: {
    title: 'Unknown',
    description: 'Use this only when the specific reason for the change is unclear or doesn\'t fit into other categories.',
    examples: ['Carrier charging us a different price that we can\'t explain']
  }
}

type ReasonKey = keyof typeof reasonExplanations

export function ReasonHelpLink () {
  const [isModalOpen, setIsModalOpen] = useState(false)

  return (
    <>
      <button
        type='button'
        onClick={() => setIsModalOpen(true)}
        className='inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 underline'
      >
        <HelpCircle size={14} />
        Which reason should I pick?
      </button>

      <Modal
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
        width='large'
      >
        <Modal.Header>
          <Modal.Title>Change Reason Guide</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div className='space-y-6'>
            <p className='text-gray-600'>
              Choose the reason that best describes why this change is being made.
              This helps maintain accurate audit records.
            </p>

            <div className='grid grid-cols-3 gap-4'>
              {[
                ...(Object.keys(reasonExplanations) as ReasonKey[]).filter(key => key !== 'unknown'),
                'unknown' as ReasonKey
              ].map((reasonKey) => {
                const reason = reasonExplanations[reasonKey]
                return (
                  <div key={reasonKey} className='border border-gray-200 rounded-lg p-4'>
                    <h3 className='font-semibold text-gray-900 mb-2'>{reason.title}</h3>
                    <p className='text-gray-700 mb-3'>{reason.description}</p>
                    <div>
                      <h4 className='text-sm font-medium text-gray-600 mb-1'>Examples:</h4>
                      <ul className='text-sm text-gray-600 list-disc list-inside space-y-1'>
                        {reason.examples.map((example, index) => (
                          <li key={index}>{example}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <button
            onClick={() => setIsModalOpen(false)}
            className='px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200'
          >
            Close
          </button>
        </Modal.Footer>
      </Modal>
    </>
  )
}
